import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
    collectKnownValuesFromGroups,
    groupCategoriesForSelect,
} from "../../helpers/categoryTree";
import {
    fetchCategoriesCached,
    subscribeCategoriesInvalidation,
} from "../../helpers/categoryApiCache";
import { CATEGORIES } from "../../constans";

/**
 * Якщо в Mongo ще немає записів для типу — опційний запасний список як у решті застосунку (CATEGORIES).
 * У модалці правил краще вимикати (fallbackToConstants=false), щоб джерело правди була лише БД.
 */
function fallbackRowsFromConstants(apiType) {
    const names = CATEGORIES[apiType];
    if (!Array.isArray(names) || !names.length) return [];
    return names.map((name, i) => ({
        _id: `fallback-${apiType}-${i}`,
        name,
        parentId: null,
        order: i * 10,
        enabled: true,
        type: apiType,
    }));
}

/**
 * Селект категорій з API з підтримкою optgroup (батько → діти).
 * apiType: business | personal | cash
 *
 * За замовчуванням кешує один запит на тип (таблиці транзакцій).
 * fetchPolicy="alwaysFresh" — завжди GET з БД при зміні apiType (наприклад модалка правила).
 * Після mutate категорій (invalidateCategoriesCache) усі екземпляри підтягують список знову.
 *
 * Якщо value є в транзакції, але його немає в дереві категорій з БД, для коректної роботи
 * нативного <select> додається прихована опція з цим самим value (не змішує список).
 * Якщо передати preserveUnknownCategory=false — синтетичної опції не буде (можливе «порожнє» поле при невідповідності).
 */
export default function CategorySelectGrouped({
    apiType,
    value,
    onChange,
    disabled,
    name = "category",
    className = "form-select form-select-sm",
    id,
    /** Додати непублікувану технічну <option hidden> для orphan value (за замовчуванням так). */
    preserveUnknownCategory = true,
    /** @type {'cached' | 'alwaysFresh'} */
    fetchPolicy = "cached",
    fallbackToConstants = true,
}) {
    const [rows, setRows] = useState([]);
    const loadSeq = useRef(0);

    const loadRows = useCallback(
        (bypassCache) => {
            if (!apiType) {
                return Promise.resolve([]);
            }
            return fetchCategoriesCached(apiType, { bypassCache }).then((data) => {
                let list = Array.isArray(data) ? data : [];
                if (!list.length && fallbackToConstants) {
                    list = fallbackRowsFromConstants(apiType);
                }
                return list;
            });
        },
        [apiType, fallbackToConstants]
    );

    const applyRows = useCallback((list) => {
        setRows(list);
    }, []);

    useEffect(() => {
        if (!apiType) {
            setRows([]);
            return undefined;
        }
        const bypass = fetchPolicy === "alwaysFresh";
        const seq = ++loadSeq.current;
        loadRows(bypass).then((list) => {
            if (seq !== loadSeq.current) return;
            applyRows(list);
        });
        return () => {
            loadSeq.current += 1;
        };
    }, [apiType, fetchPolicy, loadRows, applyRows]);

    useEffect(() => {
        if (!apiType) return undefined;

        return subscribeCategoriesInvalidation((invalidatedType) => {
            if (invalidatedType != null && invalidatedType !== apiType) return;
            const seq = ++loadSeq.current;
            loadRows(true).then((list) => {
                if (seq !== loadSeq.current) return;
                applyRows(list);
            });
        });
    }, [apiType, loadRows, applyRows]);

    const groups = useMemo(() => groupCategoriesForSelect(rows), [rows]);
    const known = useMemo(() => collectKnownValuesFromGroups(groups), [groups]);

    const orphanOption =
        preserveUnknownCategory && value && !known.has(value);

    return (
        <Form.Select
            id={id}
            name={name}
            className={className}
            value={value || ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        >
            <option value="">— не вибрано —</option>
            {orphanOption ? (
                <option value={value} hidden>
                    {value}
                </option>
            ) : null}
            {groups.map((g, idx) => {
                if (g.kind === "option") {
                    return (
                        <option key={`o-${g.value}-${idx}`} value={g.value}>
                            {g.label}
                        </option>
                    );
                }
                return (
                    <optgroup key={`g-${g.label}-${idx}`} label={g.label}>
                        {g.items.map((it) => (
                            <option key={it.value} value={it.value}>
                                {it.label}
                            </option>
                        ))}
                    </optgroup>
                );
            })}
        </Form.Select>
    );
}
